// Dashboard date range rules (Kayd's 15.10.2026 follow-up). Pure functions,
// no React, so the rules can be checked on their own. All dates are local
// calendar days (midnight), never timestamps, so "today" always means the
// admin's own calendar day.
//
//   1 day       = today
//   2–5 days    = today plus the 1–4 preceding calendar days
//   1–3 weeks   = the last 7 / 14 / 21 calendar days, including today
//   month       = one calendar month (current month ends today)
//   months      = consecutive calendar months, first to last
//   custom      = start date → end date
//
// Comparison is always the previous period of the same length, ending the
// day before the selected range starts.

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const DAY_OPTIONS = [1, 2, 3, 4, 5];
export const WEEK_OPTIONS = [1, 2, 3];
export const DEFAULT_SELECTION = { mode: 'days', n: 1 };

export function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Calendar arithmetic via setDate, so daylight-saving days are still one day.
export function addDays(d, n) {
  const x = startOfDay(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function daysInRange({ from, to }) {
  return Math.round((startOfDay(to) - startOfDay(from)) / 86400000) + 1;
}

export function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function parseISODate(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return toISODate(d) === s ? d : null; // rejects 2026-02-31 etc.
}

export function monthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthBounds(key) {
  const m = /^(\d{4})-(\d{2})$/.exec(key || '');
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  if (mo < 0 || mo > 11) return null;
  return { from: new Date(y, mo, 1), to: new Date(y, mo + 1, 0) };
}

export function monthLabel(key) {
  const b = monthBounds(key);
  return b ? `${MONTHS_LONG[b.from.getMonth()]} ${b.from.getFullYear()}` : key;
}

// The last `count` months up to and including the current one, newest first.
export function recentMonths(today = new Date(), count = 24) {
  const out = [];
  for (let i = 0; i < count; i += 1) out.push(monthKey(new Date(today.getFullYear(), today.getMonth() - i, 1)));
  return out;
}

export function formatDate(d) {
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

export function formatRange({ from, to }) {
  return daysInRange({ from, to }) === 1 ? formatDate(from) : `${formatDate(from)} – ${formatDate(to)}`;
}

export function formatShortDay(d) {
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS_SHORT[d.getMonth()]}`;
}

export function formatShortMonth(d) {
  return `${MONTHS_SHORT[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
}

// Selection -> { from, to } (inclusive local days), or null if invalid.
// A range never extends past today: there is no data for future days.
export function resolveRange(sel, today = new Date()) {
  const t = startOfDay(today);
  const capped = (from, to) => {
    const end = to > t ? t : to;
    return from > end ? null : { from, to: end };
  };
  switch (sel?.mode) {
    case 'days': {
      if (!DAY_OPTIONS.includes(sel.n)) return null;
      return { from: addDays(t, -(sel.n - 1)), to: t };
    }
    case 'weeks': {
      if (!WEEK_OPTIONS.includes(sel.n)) return null;
      return { from: addDays(t, -(7 * sel.n - 1)), to: t };
    }
    case 'month': {
      const b = monthBounds(sel.month);
      return b ? capped(b.from, b.to) : null;
    }
    case 'months': {
      const a = monthBounds(sel.fromMonth);
      const b = monthBounds(sel.toMonth);
      if (!a || !b || a.from > b.from) return null;
      return capped(a.from, b.to);
    }
    case 'custom': {
      const from = parseISODate(sel.from);
      const to = parseISODate(sel.to);
      if (!from || !to || from > to) return null;
      return capped(from, to);
    }
    default:
      return null;
  }
}

export function previousPeriod(range) {
  const len = daysInRange(range);
  const to = addDays(range.from, -1);
  return { from: addDays(to, -(len - 1)), to };
}

// Short label for the KPI cards, matching the selected period:
// "vs previous day", "vs previous 3 weeks", "vs previous 15 days".
export function comparisonLabel(sel, range) {
  if (sel.mode === 'weeks') return sel.n === 1 ? 'vs previous week' : `vs previous ${sel.n} weeks`;
  const len = daysInRange(range);
  return len === 1 ? 'vs previous day' : `vs previous ${len} days`;
}

// Name of the active selection, shown on the control itself.
export function selectionName(sel) {
  switch (sel.mode) {
    case 'days': return sel.n === 1 ? 'Today' : `Last ${sel.n} days`;
    case 'weeks': return sel.n === 1 ? 'Last week' : `Last ${sel.n} weeks`;
    case 'month': return monthLabel(sel.month);
    case 'months': return sel.fromMonth === sel.toMonth ? monthLabel(sel.fromMonth) : `${monthLabel(sel.fromMonth)} – ${monthLabel(sel.toMonth)}`;
    case 'custom': return 'Custom range';
    default: return '';
  }
}

// Every calendar day in the range, oldest first.
export function eachDay(range) {
  const out = [];
  for (let d = startOfDay(range.from); d <= range.to; d = addDays(d, 1)) out.push(d);
  return out;
}
